/**
 * <PayslipLines> — 13-ui-screens.md §12.4.
 *
 * THE RULE: this component renders whatever rows the API returns, in the
 * API's order, with the API's labels and notes. It never constructs a line,
 * never sums, never decides which statutory rows exist. A local worker's slip
 * carries EPF and PCB rows and must render without a code change.
 *
 * `kind` is `PayslipLineKind` from 12-api-contract.md §12 — the ONLY
 * vocabulary. `amount` arrives already carrying its DISPLAY sign, so
 * DEDUCTION and STATUTORY rows are negative and <Money> parenthesises and
 * reddens them with no per-kind logic here.
 *
 * `EMPLOYER_COST` is register-only and is NOT rendered on a payslip (§12.6).
 *
 * A row whose type has "Show on payslip" off never arrives as itself: the API folds
 * it into one `Other earnings` / `Other deductions` line per side in
 * `payslip.mapper.ts`, from a flag the engine froze onto the ledger row (0040).
 * Nothing to check here, and the rows shown still add up to GROSS and NET PAY.
 *
 * `antd` `<Descriptions>` has the wrong DOM for the GROSS / NET rules, which
 * need a 2 px `--brand-primary` top rule above NET PAY.
 */
import type { CSSProperties } from 'react';
import type { PayslipLineDto } from '@cropmate/shared';
import { Money, type MoneyVariant } from './Money';

const HAIRLINE = '1px solid var(--neutral-100)';
const RULE = '1px solid var(--border-default)';

interface KindStyle {
  padding: string;
  borderBottom?: string;
  borderTop?: string;
  label: CSSProperties;
  variant: MoneyVariant;
}

const STRONG_LABEL: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  fontSize: 12,
  letterSpacing: '.14em',
  color: 'var(--text-strong)',
};

const KIND: Record<PayslipLineDto['kind'], KindStyle | null> = {
  EARNING: {
    padding: '6px 0',
    borderBottom: HAIRLINE,
    label: { fontWeight: 600, color: 'var(--text-strong)', fontSize: 13 },
    variant: 'base',
  },
  DEDUCTION: {
    padding: '6px 0',
    borderBottom: HAIRLINE,
    label: { fontWeight: 600, color: 'var(--text-strong)', fontSize: 13 },
    variant: 'base',
  },
  STATUTORY: {
    padding: '6px 0',
    borderBottom: HAIRLINE,
    label: { fontWeight: 600, color: 'var(--neutral-600)', fontSize: 13 },
    variant: 'base',
  },
  ROUNDING: {
    padding: '6px 0',
    borderBottom: HAIRLINE,
    label: { fontWeight: 600, color: 'var(--neutral-600)', fontSize: 13 },
    variant: 'base',
  },
  SUBTOTAL: { padding: '10px 0', borderBottom: RULE, label: STRONG_LABEL, variant: 'strong' },
  TOTAL: {
    padding: '10px 0',
    borderBottom: RULE,
    borderTop: '2px solid var(--brand-primary)',
    label: STRONG_LABEL,
    variant: 'net',
  },
  MEMO: {
    padding: '6px 0',
    label: { fontSize: 12, color: 'var(--neutral-400)' },
    variant: 'base',
  },
  /* Register only — never on the payslip. */
  EMPLOYER_COST: null,
};

export interface PayslipLinesProps {
  lines: readonly PayslipLineDto[];
}

export function PayslipLines({ lines }: PayslipLinesProps) {
  return (
    <div style={{ padding: '6px 16px 14px' }}>
      {lines.map((line) => {
        const k = KIND[line.kind];
        if (!k) return null;
        /* BR-1 remainder disclosure (11-business-rules.md §3.4.1). */
        const note =
          line.remainderRank !== null
            ? `${line.note ? `${line.note} · ` : ''}+RM 0.01 rounding`
            : line.note;
        /* SUBTOTAL money is 16 px, TOTAL is 20 px — variants 'strong'/'net'. */
        return (
          <div
            key={`${line.sortOrder}-${line.code}`}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              padding: k.padding,
              borderBottom: k.borderBottom,
              borderTop: k.borderTop,
            }}
          >
            <span style={{ display: 'inline-flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 12 }}>
                <span style={k.label}>{line.label}</span>
                {note ? (
                  <span style={{ fontSize: 11, color: 'var(--neutral-400)' }}>{note}</span>
                ) : null}
              </span>
              {/* The note typed with this adjustment (`entryNote`) — on screen only; the PDF
               *  never prints it, so it is italic and marked as the office's own. */}
              {line.entryNote ? (
                <span
                  style={{
                    fontSize: 11,
                    fontStyle: 'italic',
                    color: 'var(--text-muted)',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                  title="Note entered with this adjustment — shown here only, not printed on the payslip."
                >
                  Note: {line.entryNote}
                </span>
              ) : null}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <Money value={line.amount} variant={k.variant} />
            </span>
          </div>
        );
      })}
    </div>
  );
}
